import { useReducer } from "react";
const ADD_ERROR = "ADD_ERROR";
const REMOVE_ERROR = "REMOVE_ERROR";
const CLEAR_ERRORS = "CLEAR_ERRORS";

interface AddAction {
  type: typeof ADD_ERROR;
  payload: Error;
}

interface RemoveAction {
  type: typeof REMOVE_ERROR;
  payload: Error;
}

interface ClearAction {
  type: typeof CLEAR_ERRORS;
}

type Action = AddAction | RemoveAction | ClearAction;

export type Error = {
  id: string;
  input: string;
  message: string;
};

type Reducer = (errors: Error[], action: Action) => Error[];
export type ServerError = {
  name: string;
  message: string;
};

const useAuthErrors = () => {
  const ACTIONS: {
    ADD_ERROR: typeof ADD_ERROR;
    REMOVE_ERROR: typeof REMOVE_ERROR;
    CLEAR_ERRORS: typeof CLEAR_ERRORS;
  } = {
    ADD_ERROR,
    REMOVE_ERROR,
    CLEAR_ERRORS,
  };

  const ERRORS: { [key: string]: Error } = {
    TERMS_AGREEMENT_ERROR: {
      id: "TERMS_AGREEMENT_ERROR",
      input: "terms-conditions",
      message: "You need to agree to our terms and conditions.",
    },
    PASSWORD_INVALID_ERROR: {
      id: "PASSWORD_INVALID_ERROR",
      input: "password",
      message: "Your password doesn't meet our policy.",
    },
    USER_EXISTS_ERROR: {
      id: "USER_EXISTS_ERROR",
      input: "email",
      message: "It looks like you already have a VideoScribe account.",
    },
    USER_NOT_CONFIRMED_ERROR: {
      id: "USER_NOT_CONFIRMED_ERROR",
      input: "general",
      message:
        "You need to verify your email address. Please check your email inbox.",
    },
    USER_NOT_FOUND_ERROR: {
      id: "USER_NOT_FOUND_ERROR",
      input: "email",
      message: "We were unable to verify your email.",
    },
    NOT_AUTHORIZED: {
      id: "NOT_AUTHORIZED",
      input: "general",
      message: "Your email address or password is incorrect.",
    },
    CODE_MISMATCH_ERROR: {
      id: "CODE_MISMATCH_ERROR",
      input: "code",
      message: "The code you entered isn't correct.",
    },
    CODE_EXPIRED_ERROR: {
      id: "CODE_EXPIRED_ERROR",
      input: "code",
      message: "The verification code you've entered has expired.",
    },
    LIMIT_EXCEEDED_ERROR: {
      id: "LIMIT_EXCEEDED_ERROR",
      input: "general",
      message: "You've sent too many requests. Please try again later.",
    },
    EMAIL_FORMAT_INVALID_ERROR: {
      id: "EMAIL_FORMAT_INVALID_ERROR",
      input: "email",
      message: "Your email address isn't a valid email address.",
    },
    FIRST_NAME_REQUIRED_ERROR: {
      id: "FIRST_NAME_REQUIRED_ERROR",
      input: "first_name",
      message: "Your first name is required.",
    },
    LAST_NAME_REQUIRED_ERROR: {
      id: "LAST_NAME_REQUIRED_ERROR",
      input: "last_name",
      message: "Your last name is required.",
    },
    EMAIL_REQUIRED_ERROR: {
      id: "EMAIL_REQUIRED_ERROR",
      input: "email",
      message: "Your email is required.",
    },
    PASSWORD_REQUIRED_ERROR: {
      id: "PASSWORD_REQUIRED_ERROR",
      input: "password",
      message: "Your password is required.",
    },
    CODE_REQUIRED_ERROR: {
      id: "CODE_REQUIRED_ERROR",
      input: "code",
      message: "Your reset code is required.",
    },
    RECAPTCHA_ERROR: {
      id: "RECAPTCHA_ERROR",
      input: "",
      message: "Failed Recaptcha.",
    },
    NETWORK_ERROR: {
      id: "NETWORK_ERROR",
      input: "network_error",
      message: "Network error.",
    },
    DEFAULT_ERROR: {
      id: "DEFAULT_ERROR",
      input: "default",
      message: "Unknown error.",
    },
  };

  const addError = (errors: Error[], error: Error) => {
    const exists = errors.find((err) => err.id === error.id);
    return exists ? errors : [...errors, error];
  };

  const removeError = (errors: Error[], error: Error) => {
    return errors.filter((err) => err.id !== error.id);
  };

  const reducer: Reducer = (errors: Error[], action: Action): Error[] => {
    switch (action.type) {
      case ACTIONS.ADD_ERROR:
        return addError(errors, action.payload);
      case ACTIONS.REMOVE_ERROR:
        return removeError(errors, action.payload);
      case ACTIONS.CLEAR_ERRORS:
        return [] as Error[];
      default:
        return errors;
    }
  };

  const [errors, dispatch] = useReducer<Reducer>(reducer, [] as Error[]);

  const getErrorsByInput = (errors: Error[], input: string) => {
    return errors.filter((err) => err.input === input);
  };

  const parseAndDispatchServerError = (error: ServerError) => {
    const { name, message } = error;

    if (name.includes("InvalidPasswordException")) {
      dispatch({
        type: ACTIONS.ADD_ERROR,
        payload: ERRORS.PASSWORD_INVALID_ERROR,
      });
      return;
    }

    if (name.includes("UsernameExistsException")) {
      dispatch({ type: ACTIONS.ADD_ERROR, payload: ERRORS.USER_EXISTS_ERROR });
      return;
    }

    if (name.includes("UserNotConfirmedException")) {
      dispatch({
        type: ACTIONS.ADD_ERROR,
        payload: ERRORS.USER_NOT_CONFIRMED_ERROR,
      });
      return;
    }

    if (
      name.includes("UserLambdaValidationException") &&
      message.includes("UsernameExistsException")
    ) {
      dispatch({ type: ACTIONS.ADD_ERROR, payload: ERRORS.USER_EXISTS_ERROR });
      return;
    }

    if (
      name.includes("UserLambdaValidationException") &&
      message.includes("failed_recaptcha")
    ) {
      dispatch({ type: ACTIONS.ADD_ERROR, payload: ERRORS.RECAPTCHA_ERROR });
      return;
    }

    if (name.includes("UserNotFoundException")) {
      dispatch({
        type: ACTIONS.ADD_ERROR,
        payload: ERRORS.USER_NOT_FOUND_ERROR,
      });
      return;
    }

    if (name.includes("NotAuthorizedException")) {
      dispatch({ type: ACTIONS.ADD_ERROR, payload: ERRORS.NOT_AUTHORIZED });
      return;
    }

    if (name.includes("CodeMismatchException")) {
      dispatch({
        type: ACTIONS.ADD_ERROR,
        payload: ERRORS.CODE_MISMATCH_ERROR,
      });
      return;
    }

    if (name.includes("ExpiredCodeException")) {
      dispatch({ type: ACTIONS.ADD_ERROR, payload: ERRORS.CODE_EXPIRED_ERROR });
      return;
    }

    if (name.includes("LimitExceededException")) {
      dispatch({
        type: ACTIONS.ADD_ERROR,
        payload: ERRORS.LIMIT_EXCEEDED_ERROR,
      });
      return;
    }

    if (
      name.includes("InvalidParameterException") &&
      message.includes("Invalid email address format")
    ) {
      dispatch({
        type: ACTIONS.ADD_ERROR,
        payload: ERRORS.EMAIL_FORMAT_INVALID_ERROR,
      });
      return;
    }

    if (name.includes("Network error") || message.includes("Network error")) {
      dispatch({ type: ACTIONS.ADD_ERROR, payload: ERRORS.NETWORK_ERROR });
      return;
    }

    dispatch({ type: ACTIONS.ADD_ERROR, payload: ERRORS.NETWORK_ERROR });
  };

  return {
    ACTIONS,
    ERRORS,
    errors,
    dispatch,
    getErrorsByInput,
    parseAndDispatchServerError,
  };
};

export default useAuthErrors;
